/* Phase 3 — Admin Notification Center: history (with retry), channel settings,
   and email/WhatsApp template editing. Email via Resend; WhatsApp is
   integration-ready (provider stub) until a Business API provider is connected. */
import { useState, useEffect } from "react";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Mail, MessageCircle, RotateCcw, Send, CheckCircle2, XCircle, Clock3, Pencil, Plug, ShieldAlert,
} from "lucide-react";

const EVENT_LABELS = {
  reschedule_submitted: "Request submitted",
  teacher_responded: "Teacher responded",
  parent_responded: "Parent responded",
  admin_decision: "Admin decision",
  timetable_updated: "Timetable updated",
  payment_reminder: "Payment reminder",
};

const ChannelIcon = ({ channel }) =>
  channel === "email"
    ? <Mail className="h-4 w-4 text-secondary" />
    : <MessageCircle className="h-4 w-4 text-[#2D6A4F]" />;

// ------------------------------------------------------------------ history tab
function HistoryTab() {
  const [status, setStatus] = useState("all");
  const [channel, setChannel] = useState("all");
  const [event, setEvent] = useState("all");
  const params = new URLSearchParams();
  if (status !== "all") params.set("status", status);
  if (channel !== "all") params.set("channel", channel);
  if (event !== "all") params.set("event_type", event);
  const qs = params.toString();
  const { data: logs, loading, reload } = useList(`/notification-logs${qs ? `?${qs}` : ""}`, [qs]);
  const { data: stats, reload: reloadStats } = useList("/notification-logs/stats");
  const [busy, setBusy] = useState(null);

  const retry = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/notification-logs/${id}/retry`);
      toast[data.status === "sent" ? "success" : "warning"](
        data.status === "sent" ? "Notification sent" : `Still ${data.status}: ${data.error || ""}`);
      reload(); reloadStats();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const retryAll = async (target) => {
    setBusy(target);
    try {
      const { data } = await api.post(`/notification-logs/retry-failed?status=${target}`);
      toast.info(`Retried ${data.attempted} — sent ${data.sent}, failed ${data.failed}, pending ${data.pending}`);
      reload(); reloadStats();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Send} label="Total" value={stats?.total ?? "—"} testId="notif-stat-total" />
        <StatCard icon={CheckCircle2} label="Sent" value={stats?.sent ?? "—"} testId="notif-stat-sent" />
        <StatCard icon={Clock3} label="Pending" value={stats?.pending ?? "—"} testId="notif-stat-pending" />
        <StatCard icon={XCircle} label="Failed" value={stats?.failed ?? "—"} testId="notif-stat-failed" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36" data-testid="notif-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-36" data-testid="notif-filter-channel"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All channels</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
        <Select value={event} onValueChange={setEvent}>
          <SelectTrigger className="w-48" data-testid="notif-filter-event"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            {Object.entries(EVENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => retryAll("failed")} disabled={busy === "failed"}
          data-testid="retry-all-failed-button">
          <RotateCcw className="h-4 w-4 mr-1" />Retry all failed
        </Button>
        <Button variant="outline" size="sm" onClick={() => retryAll("pending")} disabled={busy === "pending"}
          data-testid="retry-all-pending-button">
          <RotateCcw className="h-4 w-4 mr-1" />Retry all pending
        </Button>
      </div>

      {loading ? <Spinner full /> : !logs || logs.length === 0 ? (
        <EmptyState text="No notifications logged yet — they appear when reschedule events happen." />
      ) : (
        <div className="space-y-2 stagger-in" data-testid="notification-log-list">
          {logs.map((l) => (
            <div key={l.id} className="rounded-lg border bg-card p-4 flex flex-wrap items-start gap-3"
              data-testid={`notif-log-${l.id}`}>
              <ChannelIcon channel={l.channel} />
              <div className="flex-1 min-w-[260px]">
                <p className="text-sm font-medium">
                  {l.recipient_name} <span className="text-muted-foreground font-normal">· {l.recipient_role}</span>
                  <span className="text-xs text-muted-foreground font-mono ml-2">{l.recipient_address}</span>
                </p>
                <p className="text-xs mt-0.5">{l.subject || l.body?.slice(0, 110)}</p>
                {l.error && <p className="text-xs text-destructive mt-1" data-testid={`notif-error-${l.id}`}>{l.error}</p>}
                <p className="text-[11px] text-muted-foreground mt-1">
                  {EVENT_LABELS[l.event_type] || l.event_type} · {l.created_at?.slice(0, 16).replace("T", " ")}
                  {l.retry_count > 0 && ` · retried ${l.retry_count}×`}
                </p>
              </div>
              <StatusBadge status={l.status} testId={`notif-status-${l.id}`} />
              {l.status !== "sent" && (
                <Button variant="ghost" size="sm" onClick={() => retry(l.id)} disabled={busy === l.id}
                  data-testid={`retry-${l.id}`}>
                  <RotateCcw className="h-4 w-4 mr-1" />Retry
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function TestEmailCard() {
  const [to, setTo] = useState("yamaha.atria@gmail.com");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/email/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/email/test", { to, bypass_sandbox: true });
      setResult(data);
      if (data.status === "sent") {
        toast.success(`Sent via ${data.provider} — check ${to}`);
      } else if (data.status === "failed") {
        toast.error(data.error || "Send failed");
      } else {
        toast.info(`Status: ${data.status}`);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-5" data-testid="test-email-card">
      <div className="flex items-start gap-4">
        <Send className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold">Send test email</p>
          <p className="text-xs text-muted-foreground mb-3">
            Sends one email via the currently-configured provider
            {cfg?.email_provider && cfg.email_provider !== "none"
              ? <> (<span className="font-mono">{cfg.email_provider}</span>{cfg.email_from ? <>, from <span className="font-mono">{cfg.email_from}</span></> : null})</>
              : null}
            . Bypasses sandbox for this send.
            Use it to verify Resend/DNS after adding your API key.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Recipient</Label>
              <Input type="email" value={to} onChange={(e) => setTo(e.target.value)}
                     placeholder="you@example.com"
                     data-testid="test-email-recipient-input" />
            </div>
            <Button onClick={send} disabled={sending || !to}
                    data-testid="test-email-send-btn">
              {sending ? "Sending…" : "Send Test"}
            </Button>
          </div>
          {result && (
            <div className={`mt-3 p-3 rounded text-xs ${
              result.status === "sent" ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : result.status === "failed" ? "bg-rose-50 text-rose-800 border border-rose-200"
              : "bg-amber-50 text-amber-800 border border-amber-200"
            }`} data-testid="test-email-result">
              <p className="font-semibold">Status: {result.status}</p>
              {result.provider_message_id && (
                <p>Provider message ID: <span className="font-mono">{result.provider_message_id}</span></p>
              )}
              <p>Provider: <span className="font-mono">{result.provider}</span></p>
              {result.error && <p className="mt-1">Error: {result.error}</p>}
              <p className="mt-1 text-muted-foreground">
                Log ID: <span className="font-mono">{result.log_id}</span>
                {" — "}
                view under &quot;History&quot; tab.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ settings tab
function SettingsTab() {
  const { data: settings, reload } = useList("/notification-settings");
  const [busy, setBusy] = useState(false);

  const save = async (patch) => {
    setBusy(true);
    try {
      await api.put("/notification-settings", {
        email_enabled: settings.email_enabled,
        whatsapp_enabled: settings.whatsapp_enabled,
        whatsapp_provider: settings.whatsapp_provider || "none",
        auto_reminder_enabled: settings.auto_reminder_enabled ?? true,
        reminder_days_before: settings.reminder_days_before ?? 3,
        email_sandbox_mode: settings.email_sandbox_mode ?? true,
        email_sandbox_address: settings.email_sandbox_address ?? "",
        ...patch,
      });
      toast.success("Notification settings saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!settings) return <Spinner full />;
  const sandboxOn = settings.email_sandbox_mode ?? true;
  return (
    <div className="max-w-2xl space-y-4">
      <div className={`rounded-lg border p-5 ${sandboxOn ? "border-[#B45309]/50 bg-[#FFF8EF]" : "bg-card"}`}
        data-testid="email-sandbox-card">
        <div className="flex items-center gap-4">
          <ShieldAlert className={`h-5 w-5 shrink-0 ${sandboxOn ? "text-[#B45309]" : "text-muted-foreground"}`} />
          <div className="flex-1">
            <p className="text-sm font-semibold">
              Email sandbox mode {sandboxOn ? "— ON (safe testing)" : "— OFF (LIVE emails to real parents)"}
            </p>
            <p className="text-xs text-muted-foreground">
              {sandboxOn
                ? "Every outbound email is rerouted to the sandbox address below — real parents receive NOTHING. If no address is set, all emails are blocked."
                : "Emails deliver to real recipients. Turn sandbox ON while testing to protect real parents."}
            </p>
          </div>
          <Switch checked={sandboxOn} disabled={busy}
            onCheckedChange={(v) => save({ email_sandbox_mode: v })} data-testid="email-sandbox-switch" />
        </div>
        {sandboxOn && (
          <div className="flex items-center gap-2 mt-3 pl-9">
            <Input type="email" placeholder="sandbox@yourschool.com" className="max-w-xs" disabled={busy}
              defaultValue={settings.email_sandbox_address || ""}
              onBlur={(e) => e.target.value.trim() !== (settings.email_sandbox_address || "") && save({ email_sandbox_address: e.target.value.trim() })}
              data-testid="email-sandbox-address-input" />
            <span className="text-xs text-muted-foreground">
              {settings.email_sandbox_address ? "All test emails go here." : "No address — all emails are blocked."}
            </span>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card p-5 flex items-center gap-4" data-testid="email-channel-card">
        <Mail className="h-5 w-5 text-secondary shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold">Email notifications</p>
          <p className="text-xs text-muted-foreground">
            Sent via Resend. {settings.email_configured
              ? "Provider configured — emails deliver live."
              : 'Emails stay "pending" (retryable) until a RESEND_API_KEY is configured on the server.'}
          </p>
        </div>
        <Switch checked={settings.email_enabled} disabled={busy}
          onCheckedChange={(v) => save({ email_enabled: v })} data-testid="email-enabled-switch" />
      </div>

      <TestEmailCard />

      <div className="rounded-lg border bg-card p-5 flex items-center gap-4" data-testid="whatsapp-channel-card">
        <MessageCircle className="h-5 w-5 text-[#2D6A4F] shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold">WhatsApp notifications</p>
          <p className="text-xs text-muted-foreground">
            {settings.whatsapp_configured
              ? "Meta Cloud API configured — messages deliver live (free-form texts require an active 24h customer window)."
              : 'Messages are logged and queued as "pending" until provider credentials are added.'}
          </p>
        </div>
        <Switch checked={settings.whatsapp_enabled} disabled={busy}
          onCheckedChange={(v) => save({ whatsapp_enabled: v })} data-testid="whatsapp-enabled-switch" />
      </div>

      <div className="rounded-lg border bg-card p-5 flex flex-wrap items-center gap-4" data-testid="whatsapp-provider-card">
        <Plug className="h-5 w-5 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-[260px]">
          <p className="text-sm font-semibold">WhatsApp provider</p>
          <p className="text-xs text-muted-foreground">
            {settings.whatsapp_provider === "meta_cloud"
              ? settings.whatsapp_configured
                ? "Meta Cloud API connected and ready."
                : "Meta Cloud API selected. To go live: developers.facebook.com → your Business app → WhatsApp → API Setup, then add WHATSAPP_ACCESS_TOKEN and WHATSAPP_PHONE_NUMBER_ID to the server config. Queued messages can then be retried in one click."
              : "No provider — messages stay queued as pending."}
          </p>
        </div>
        <Select value={settings.whatsapp_provider || "none"} disabled={busy}
          onValueChange={(v) => save({ whatsapp_provider: v })}>
          <SelectTrigger className="w-44" data-testid="whatsapp-provider-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(settings.whatsapp_providers || ["none", "meta_cloud"]).map((p) => (
              <SelectItem key={p} value={p}>{p === "meta_cloud" ? "Meta Cloud API" : "None"}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border bg-card p-5 flex flex-wrap items-center gap-4" data-testid="auto-reminder-card">
        <Clock3 className="h-5 w-5 text-secondary shrink-0" />
        <div className="flex-1 min-w-[260px]">
          <p className="text-sm font-semibold">Automatic payment reminders</p>
          <p className="text-xs text-muted-foreground">
            A daily job reminds parents of unpaid invoices due within the window below (and overdue ones),
            skipping anyone reminded in the last 3 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="number" min={0} max={30} className="w-20" disabled={busy}
            value={settings.reminder_days_before ?? 3}
            onChange={(e) => save({ reminder_days_before: parseInt(e.target.value || "0", 10) })}
            data-testid="reminder-days-input" />
          <span className="text-xs text-muted-foreground">days before due</span>
          <Switch checked={settings.auto_reminder_enabled ?? true} disabled={busy}
            onCheckedChange={(v) => save({ auto_reminder_enabled: v })}
            data-testid="auto-reminder-switch" />
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ templates tab
function TemplatesTab() {
  const { data: templates, loading, reload } = useList("/notification-templates");
  const [editing, setEditing] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const openEdit = (t) => { setEditing(t); setSubject(t.subject || ""); setBody(t.body); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/notification-templates/${editing.id}`, {
        subject: editing.channel === "email" ? subject : null, body,
      });
      toast.success("Template updated");
      setEditing(null); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (loading) return <Spinner full />;
  return (
    <div className="space-y-2 stagger-in" data-testid="template-list">
      {(templates || []).map((t) => (
        <div key={t.id} className="rounded-lg border bg-card p-4 flex flex-wrap items-center gap-3"
          data-testid={`template-${t.event_type}-${t.channel}`}>
          <ChannelIcon channel={t.channel} />
          <div className="flex-1 min-w-[260px]">
            <p className="text-sm font-medium">{EVENT_LABELS[t.event_type] || t.event_type}
              <span className="text-xs text-muted-foreground ml-2 uppercase">{t.channel}</span></p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.subject || t.body}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => openEdit(t)}
            data-testid={`edit-template-${t.event_type}-${t.channel}`}>
            <Pencil className="h-4 w-4 mr-1" />Edit
          </Button>
        </div>
      ))}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl" data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing && `${EVENT_LABELS[editing.event_type]} — ${editing.channel} template`}
            </DialogTitle>
            <DialogDescription>Edit the message sent for this event. Variables are replaced at send time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {editing?.channel === "email" && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="template-subject-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message body</Label>
              <Textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} data-testid="template-body-input" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Variables: {(editing?.variables || []).map((v) => `{{${v}}}`).join(", ")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !body} data-testid="save-template-button">
              {busy ? "Saving..." : "Save template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------------ page
export function NotificationCenterPage() {
  return (
    <div data-testid="notification-center-page">
      <PageHeader title="Notification center"
        description="Email & WhatsApp notifications for reschedule events — delivery history, retry, channel settings and message templates." />
      <Tabs defaultValue="history">
        <TabsList className="mb-6">
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="history"><HistoryTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
